// <AvailabilityCard> — Home View Step 4 + 7 (T-043 + T-046).
//
// Renders "People's availability today" — server-grouped rows of policy
// → people-pile. Mirrors spec §5 layout: header, divided rows, Show
// more / Show less footer (initial 3, expand to all), empty state.
//
// T-046 polish: portal-rendered dark tooltip (spec §5.4) listing every
// person on the row (name + 24×24 avatar + date range), with auto-flip
// placement above/below the cluster, hover/focus open + blur/Esc close,
// 150 ms fade + 4 px y-shift on enter/exit.

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { HomeAvailabilityRow } from '@pa/shared';
import { Avatar } from './Avatar';

export interface AvailabilityCardProps {
  rows: HomeAvailabilityRow[];
}

const INITIAL_ROWS = 3;
const PILE_VISIBLE = 5;
const TOOLTIP_WIDTH = 220;
const TOOLTIP_GAP = 10; // distance between cluster + tooltip + tail
const TOOLTIP_BELOW_THRESHOLD = 220; // spec §5.4 — flip above when < 220 px below

export function AvailabilityCard({ rows }: AvailabilityCardProps) {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? rows : rows.slice(0, INITIAL_ROWS);
  const hiddenCount = Math.max(0, rows.length - INITIAL_ROWS);

  return (
    <section
      data-testid="home-availability"
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
    >
      <header className="p-4 border-b border-slate-100 flex justify-between items-center">
        <h3 className="text-sm font-bold text-slate-800">
          People's availability today
        </h3>
      </header>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-slate-400 italic">
          No one's off today
        </div>
      ) : (
        <>
          <ul className="divide-y divide-slate-50">
            {visibleRows.map((row) => (
              <li
                key={row.policyName}
                className="px-4 py-3 flex justify-between items-center"
              >
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <span aria-hidden>{row.emoji}</span>
                  {row.policyName}
                </span>
                <AvatarPile row={row} />
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="w-full py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 transition-colors border-t border-slate-100"
              data-testid="home-availability-show-more"
            >
              {expanded ? 'Show less' : `Show more (${hiddenCount} more)`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function AvatarPile({ row }: { row: HomeAvailabilityRow }) {
  // Server pre-truncates to 5; if `count > people.length`, the 6th slot
  // is the "+N" overflow chip per spec §5.3.
  const visible = row.people.slice(0, PILE_VISIBLE);
  const overflow = Math.max(0, row.count - visible.length);
  const everyName = row.people.map((p) => p.name).join(', ');
  const labelTail = overflow > 0 ? ` and ${overflow} more` : '';
  const accessibleLabel = `${row.count} ${row.count === 1 ? 'person' : 'people'} on ${row.policyName}: ${everyName}${labelTail}`;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const tooltipId = useId();

  function show() {
    setOpen(true);
  }
  function hide() {
    setOpen(false);
  }
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((v) => !v);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div
      ref={wrapperRef}
      tabIndex={0}
      className="flex items-center outline-none focus-visible:ring-2 focus-visible:ring-[#6344C6]/40 rounded-full"
      role="group"
      aria-label={accessibleLabel}
      aria-describedby={open ? tooltipId : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={onKeyDown}
    >
      {visible.map((person, idx) => (
        <Avatar
          key={person.id}
          name={person.name}
          avatarUrl={person.avatarUrl}
          sizeClass="w-7 h-7"
          textSizeClass="text-[10px]"
          className="border-2 border-white"
          style={{
            marginLeft: idx === 0 ? 0 : -8,
            zIndex: visible.length - idx,
          }}
        />
      ))}
      {overflow > 0 && (
        <span
          data-testid="home-availability-overflow"
          className="w-7 h-7 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-slate-500"
          style={{
            marginLeft: visible.length === 0 ? 0 : -8,
            zIndex: 0,
          }}
        >
          +{overflow}
        </span>
      )}
      <AvatarPileTooltip
        id={tooltipId}
        open={open}
        anchorRef={wrapperRef}
        people={row.people}
        policyName={row.policyName}
      />
    </div>
  );
}

function AvatarPileTooltip({
  id,
  open,
  anchorRef,
  people,
  policyName,
}: {
  id: string;
  open: boolean;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  people: HomeAvailabilityRow['people'];
  policyName: string;
}) {
  const [pos, setPos] = useState<{
    top: number;
    right: number;
    placeAbove: boolean;
  } | null>(null);
  // Two-phase mount to drive the 150 ms fade+shift transition: paint
  // first with `mounted=false` (opacity 0, shifted), then flip on the
  // next frame so CSS interpolates to the open state.
  const [mounted, setMounted] = useState(false);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setMounted(false);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < TOOLTIP_BELOW_THRESHOLD;
    setPos({
      top: placeAbove ? rect.top - TOOLTIP_GAP : rect.bottom + TOOLTIP_GAP,
      right: window.innerWidth - rect.right,
      placeAbove,
    });
  }, [open, anchorRef, people.length]);

  useEffect(() => {
    if (!open) {
      setMounted(false);
      return;
    }
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Always emit the wrapper element with `data-open` so component
  // tests can assert its open/closed state without polling for
  // mount/unmount, but only paint the inner content when open. That
  // way `getByText(policyName)` in tests doesn't double-match (the
  // outer card row already renders the same string).
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="tooltip"
      id={id}
      data-testid="home-availability-tooltip"
      data-open={open}
      style={
        open && pos
          ? {
              position: 'fixed',
              top: pos.placeAbove ? undefined : pos.top,
              bottom: pos.placeAbove
                ? window.innerHeight - pos.top
                : undefined,
              right: pos.right,
              width: TOOLTIP_WIDTH,
              zIndex: 9999,
              opacity: mounted ? 1 : 0,
              transform: mounted
                ? 'translateY(0)'
                : pos.placeAbove
                  ? 'translateY(4px)'
                  : 'translateY(-4px)',
              pointerEvents: mounted ? 'auto' : 'none',
              transition: 'opacity 150ms ease, transform 150ms ease',
            }
          : { display: 'none' }
      }
      className="bg-slate-900 text-white rounded-xl shadow-2xl py-2"
    >
      {open ? (
        <>
          <p className="px-3 pb-1 text-[10px] uppercase tracking-wider text-white/60">
            {policyName}
          </p>
          <ul>
            {people.map((p) => (
              <li key={p.id} className="flex items-center gap-2 px-3 py-1.5">
                <Avatar
                  name={p.name}
                  avatarUrl={p.avatarUrl}
                  sizeClass="w-6 h-6"
                  textSizeClass="text-[10px]"
                  className="border border-white/10"
                />
                <div className="min-w-0">
                  <p className="text-xs font-medium leading-tight truncate">
                    {p.name}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {formatDateRange(p.startDate, p.endDate)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>,
    document.body,
  );
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Spec §5.4 format: "Feb 4" for single-day, "Feb 4 – Feb 11" for ranges
// (en-dash). ISO strings → "Mon Day".
function formatDateRange(startIso: string, endIso: string): string {
  const start = formatMmmDay(startIso);
  if (!start) return '';
  if (startIso === endIso) return start;
  const end = formatMmmDay(endIso);
  if (!end) return start;
  return `${start} \u2013 ${end}`;
}

function formatMmmDay(iso: string): string | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const monthIdx = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (monthIdx < 0 || monthIdx > 11) return null;
  return `${MONTHS_SHORT[monthIdx]} ${day}`;
}
